import { useNavigate } from 'react-router';
import { funnelStep } from '../../analytics/funnel';
import { AD_DEMOS, type AdDemo } from '../../journey/demos';
import { Screen } from '../../ui';

/**
 * /new/start — "What do you want to make?" (UGC, 2026-11-02: the owner dropped the target markets).
 *
 * Where a creator's own link lands (journey/creatorLinks.ts, `/go/<handle>`): every example we have, each a
 * real published app with its picture, and one tap starts that example's journey — its demo, "What is it?",
 * the questions and the build, as an ad's link does. The creator's tag is already the visit's (read on the
 * first page, analytics/visits.ts), so it follows them through. "Something else" goes to "What is it?" empty.
 */
const STARTER: Record<string, string> = {
  Wedding: 'wedding', Trip: 'trip', Potluck: 'potluck', Volunteer: 'volunteer', Schedule: 'cafe', Golf: 'golf',
};
const TITLE: Record<string, string> = {
  Wedding: 'A wedding website with RSVPs',
  Trip: 'A group trip plan',
  Potluck: 'A potluck sign-up',
  Volunteer: 'A volunteer shift sign-up',
  Schedule: 'A staff schedule',
  Golf: 'A golf round tracker',
};

export function startOrder(demos: AdDemo[] = AD_DEMOS): AdDemo[] {
  // The AI examples first (spec 2026-11-02 §4), as they are added; then the rest in the order they were made.
  return [...demos].sort((a, b) => Number(!!b.ai) - Number(!!a.ai));
}

export default function Start() {
  const navigate = useNavigate();
  function choose(demo: AdDemo | null): void {
    funnelStep('starter_chosen', { starter: (demo ? (STARTER[demo.ad] ?? demo.starter) : 'other') as never });
    if (!demo) {
      navigate('/new/what');
      return;
    }
    navigate(`/new/idea?${new URLSearchParams({ idea: demo.sentence }).toString()}`);
  }
  return (
    <Screen title="What do you want to make?">
      <p className="type-body start-sub">Pick one to start from. You'll make it your own in a few questions.</p>
      <ul className="start-grid" aria-label="Examples">
        {startOrder().map((demo) => (
          <li key={demo.slug}>
            <button type="button" className="start-card" onClick={() => choose(demo)} aria-label={demo.cta}>
              <img className="start-poster" src={demo.poster} alt="" loading="lazy" />
              <span className="type-heading start-title">{demo.title ?? TITLE[demo.ad] ?? demo.ad}</span>
              {demo.ai && <span className="type-caption start-badge">Uses AI</span>}
            </button>
          </li>
        ))}
      </ul>
      <p className="start-other">
        <button type="button" className="link-button" onClick={() => choose(null)}>Something else</button>
      </p>
    </Screen>
  );
}
